/**
 * PNG → ICO conversion utilities.
 *
 * ICO file format (PNG-encoded, supported on Windows Vista+):
 *   Header          : 6 bytes
 *     uint16  reserved (0)
 *     uint16  type    (1 = ICO)
 *     uint16  count   (number of images)
 *   Directory entry : 16 bytes per image
 *     uint8   width        (0 means 256)
 *     uint8   height       (0 means 256)
 *     uint8   colorCount   (0 when > 256 colors / truecolor)
 *     uint8   reserved     (0)
 *     uint16  planes       (1)
 *     uint16  bitCount     (32)
 *     uint32  bytesInRes   (size of image data in bytes)
 *     uint32  imageOffset  (offset from start of file)
 *   Image data       : raw PNG bytes per size, concatenated.
 */

const HEADER_SIZE = 6;
const DIR_ENTRY_SIZE = 16;

function loadImageFromFile(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => resolve({ img, url });
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error(`Failed to decode image: ${file.name}`));
    };
    img.src = url;
  });
}

function renderPngAtSize(img, size) {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      reject(new Error('Failed to obtain 2D canvas context'));
      return;
    }
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.clearRect(0, 0, size, size);
    ctx.drawImage(img, 0, 0, size, size);
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error(`Failed to encode PNG for size ${size}`));
          return;
        }
        blob.arrayBuffer()
          .then((buf) => resolve(new Uint8Array(buf)))
          .catch(reject);
      },
      'image/png'
    );
  });
}

/**
 * Render the given PNG File to a list of { size, bytes } entries, where bytes
 * are the raw PNG-encoded payload for that size. The image is decoded once
 * and re-rendered onto a fresh canvas for each requested size.
 * @param {File} file
 * @param {number[]} sizes
 * @returns {Promise<Array<{size: number, bytes: Uint8Array}>>}
 */
export async function renderPngToSizes(file, sizes) {
  if (!file) throw new Error('No file provided');
  if (!Array.isArray(sizes) || sizes.length === 0) {
    throw new Error('At least one output size is required');
  }

  const validSizes = sizes
    .map((s) => Number(s))
    .filter((s) => Number.isFinite(s) && s > 0)
    .map((s) => (s > 255 ? 256 : Math.round(s)));
  if (validSizes.length === 0) {
    throw new Error('No valid sizes provided');
  }

  const { img, url } = await loadImageFromFile(file);
  try {
    const out = [];
    for (const size of validSizes) {
      // eslint-disable-next-line no-await-in-loop
      const bytes = await renderPngAtSize(img, size);
      out.push({ size, bytes });
    }
    return out;
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Assemble an ICO Blob from one or more { size, bytes } entries.
 * @param {Array<{size: number, bytes: Uint8Array}>} entries
 * @returns {Blob}
 */
export function buildIcoBlob(entries) {
  if (!Array.isArray(entries) || entries.length === 0) {
    throw new Error('buildIcoBlob: entries required');
  }
  const totalImageBytes = entries.reduce((sum, e) => sum + e.bytes.length, 0);
  const totalSize = HEADER_SIZE + DIR_ENTRY_SIZE * entries.length + totalImageBytes;
  const buffer = new ArrayBuffer(totalSize);
  const view = new DataView(buffer);
  const out = new Uint8Array(buffer);

  view.setUint16(0, 0, true);                  // reserved
  view.setUint16(2, 1, true);                  // type = 1 (ICO)
  view.setUint16(4, entries.length, true);

  let dataOffset = HEADER_SIZE + DIR_ENTRY_SIZE * entries.length;
  entries.forEach(({ size, bytes }, i) => {
    const dirOffset = HEADER_SIZE + i * DIR_ENTRY_SIZE;
    const w = size === 256 ? 0 : size;
    const h = size === 256 ? 0 : size;
    view.setUint8(dirOffset + 0, w);
    view.setUint8(dirOffset + 1, h);
    view.setUint8(dirOffset + 2, 0);             // colorCount
    view.setUint8(dirOffset + 3, 0);             // reserved
    view.setUint16(dirOffset + 4, 1, true);      // planes
    view.setUint16(dirOffset + 6, 32, true);     // bitCount
    view.setUint32(dirOffset + 8, bytes.length, true);
    view.setUint32(dirOffset + 12, dataOffset, true);
    out.set(bytes, dataOffset);
    dataOffset += bytes.length;
  });

  return new Blob([buffer], { type: 'image/x-icon' });
}

/** Build a preview data URL for a File. */
export function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error || new Error('FileReader error'));
    reader.readAsDataURL(file);
  });
}

/** Read the natural pixel dimensions of an image File. */
export function readImageDims(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const dims = { width: img.naturalWidth, height: img.naturalHeight };
      URL.revokeObjectURL(url);
      resolve(dims);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to decode image'));
    };
    img.src = url;
  });
}

/** Human-readable file size. */
export function formatBytes(bytes) {
  if (!Number.isFinite(bytes) || bytes < 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0;
  let n = bytes;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i += 1;
  }
  return `${n.toFixed(n >= 10 || i === 0 ? 0 : 2)} ${units[i]}`;
}

/** Strip a file extension and return the base name. */
export function baseName(name) {
  const dot = name.lastIndexOf('.');
  return dot <= 0 ? name : name.slice(0, dot);
}
